import { Injectable } from '@angular/core';
import { UserService } from '../user/user.service';

export type ThemeMode = 'light' | 'dark' | 'system';

function setCrewdleCookie(name: string, value: string): void {
  try {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000);
    document.cookie = `crewdle_${name}=${encodeURIComponent(value)};expires=${date.toUTCString()};path=/;domain=.crewdle.ai;SameSite=Lax`;
  } catch {}
}

function getCrewdleCookie(name: string): string | null {
  try {
    const prefix = `crewdle_${name}=`;
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmed = cookie.trim();
      if (trimmed.startsWith(prefix)) {
        return decodeURIComponent(trimmed.substring(prefix.length));
      }
    }
  } catch {}
  return null;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  constructor(
    private userService: UserService,
  ) {
    // Migrate legacy 'darkMode' key to standard 'theme' key
    const legacyMode = window.localStorage.getItem('darkMode');
    if (legacyMode && !window.localStorage.getItem('theme')) {
      const theme = legacyMode === 'true' ? 'dark' : 'light';
      window.localStorage.setItem('theme', theme);
    }

    // Sync theme from cookie when returning to this tab (cross-platform sync)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      const cookieTheme = getCrewdleCookie('theme');
      if (cookieTheme && ['light', 'dark', 'system'].includes(cookieTheme)) {
        const currentTheme = this.getThemeMode();
        if (cookieTheme !== currentTheme) {
          const isDark = cookieTheme === 'dark' || (cookieTheme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
          window.localStorage.setItem('theme', cookieTheme);
          window.localStorage.setItem('darkMode', isDark ? 'true' : 'false');
          this.applyTheme(isDark, false, false);
        }
      }
    });
  }

  isDarkMode() {
    const themeMode = window.localStorage.getItem('theme');
    if (themeMode && ['light', 'dark', 'system'].includes(themeMode)) {
      if (themeMode === 'dark') return true;
      if (themeMode === 'light') return false;
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }

    // Fallback to legacy key
    const hasLocalStorageMode = window.localStorage.getItem('darkMode');
    if (hasLocalStorageMode) {
      return hasLocalStorageMode === 'true';
    }

    // Check root domain cookie
    const cookieTheme = getCrewdleCookie('theme');
    if (cookieTheme) {
      if (cookieTheme === 'dark') return true;
      if (cookieTheme === 'light') return false;
      // 'system' falls through to system preference
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  getThemeMode(): ThemeMode {
    const themeMode = window.localStorage.getItem('theme');
    if (themeMode && ['light', 'dark', 'system'].includes(themeMode)) {
      return themeMode as ThemeMode;
    }

    // Fallback to legacy key
    const hasLocalStorageMode = window.localStorage.getItem('darkMode');
    if (hasLocalStorageMode) {
      return hasLocalStorageMode === 'true' ? 'dark' : 'light';
    }

    const cookieTheme = getCrewdleCookie('theme');
    if (cookieTheme && ['light', 'dark', 'system'].includes(cookieTheme)) {
      return cookieTheme as ThemeMode;
    }

    return 'system';
  }

  async applyTheme(darkMode?: boolean, saveSetting = false, checkUser = true) {
    if (darkMode === undefined) {
      darkMode = this.isDarkMode();
    }

    if (saveSetting) {
      const themeMode: ThemeMode = darkMode ? 'dark' : 'light';
      await this.userService.updateThemePreference(themeMode);
      window.localStorage.setItem('theme', themeMode);
      window.localStorage.setItem('darkMode', darkMode ? 'true' : 'false');
      setCrewdleCookie('theme', themeMode);
    }

    if (darkMode) {
      document.body.classList.add('dark-mode');
      document.documentElement.classList.add('dark');
    } else {
      document.body.classList.remove('dark-mode');
      document.documentElement.classList.remove('dark');
    }

    if (saveSetting || !checkUser) {
      return;
    }

    const recordedMode = await this.userService.getThemePreference();
    if (!recordedMode) {
      return;
    }

    const isRecordedDarkMode = recordedMode === 'dark';
    if (darkMode !== isRecordedDarkMode) {
      this.applyTheme(isRecordedDarkMode, true);
    }
  }
}
